const PLATFORM_LABELS = {
  telegram_userbot: 'Telegram Userbot',
};

const STATUS_LABELS = {
  connected: 'Connected',
  connecting: 'Connecting…',
  disconnected: 'Offline',
  needs_login: 'Setup required',
  error: 'Error',
};

const TRIGGER_LABELS = {
  direct: 'Direct address',
  question: 'Questions',
  all: 'All messages',
};

export function platformLabel(platform) {
  return PLATFORM_LABELS[platform] || platform;
}

export function botStatusLabel(status, hasCredentials = true) {
  if (!hasCredentials && status !== 'connected') {
    return 'Setup required';
  }
  return STATUS_LABELS[status] || 'Unknown';
}

export function botStatusColor(status, hasCredentials = true) {
  if (!hasCredentials && status !== 'connected') return 'orange';
  const map = {
    connected: 'green',
    connecting: 'processing',
    disconnected: 'default',
    needs_login: 'orange',
    error: 'red',
  };
  return map[status] || 'default';
}

export function botTriggerLabels(triggerConfig, legacyMode) {
  const triggers = triggerConfig?.triggers;
  if (Array.isArray(triggers) && triggers.length) {
    return triggers.map((t) => TRIGGER_LABELS[t] || t);
  }
  if (legacyMode) {
    return [TRIGGER_LABELS[legacyMode] || legacyMode];
  }
  return [TRIGGER_LABELS.direct];
}

export function defaultTriggerConfig() {
  return {
    triggers: ['direct'],
    question_detection: 'heuristic',
    skip_replies_to_others: true,
    question_llm: {
      source: 'local',
      model: '',
      cloud_provider_id: null,
    },
  };
}

export function normalizeTriggerConfigFromApi(triggerConfig, legacyMode) {
  const base = defaultTriggerConfig();
  const cfg = triggerConfig && typeof triggerConfig === 'object' ? triggerConfig : {};
  let triggers = Array.isArray(cfg.triggers) && cfg.triggers.length ? [...cfg.triggers] : [];
  if (!triggers.length && legacyMode) {
    triggers = [legacyMode];
  }
  if (!triggers.length) {
    triggers = ['direct'];
  }
  if (triggers.includes('all')) {
    triggers = ['all'];
  }
  return {
    triggers,
    question_detection: cfg.question_detection || base.question_detection,
    skip_replies_to_others:
      cfg.skip_replies_to_others !== undefined
        ? Boolean(cfg.skip_replies_to_others)
        : base.skip_replies_to_others,
    question_llm: {
      source: cfg.question_llm?.source || base.question_llm.source,
      model: cfg.question_llm?.model || '',
      cloud_provider_id: cfg.question_llm?.cloud_provider_id ?? null,
    },
  };
}

export function buildTriggerConfigPayload(
  formTriggers,
  questionDetection,
  questionLlm,
  skipRepliesToOthers = true,
) {
  let triggers = [...(formTriggers || [])];
  if (triggers.includes('all')) {
    triggers = ['all'];
  }
  if (!triggers.length) {
    triggers = ['direct'];
  }
  return {
    triggers,
    question_detection: questionDetection || 'heuristic',
    skip_replies_to_others: Boolean(skipRepliesToOthers),
    question_llm: questionLlm || defaultTriggerConfig().question_llm,
  };
}

/** Telegram chat IDs are signed integers (users, groups, supergroups -100…). */
const TELEGRAM_CHAT_ID_RE = /^-?(?:0|[1-9]\d{0,18})$/;

export function isValidTelegramChatId(value) {
  const s = String(value ?? '').trim();
  return Boolean(s) && TELEGRAM_CHAT_ID_RE.test(s);
}

/** Normalize tag input to unique string chat IDs. */
export function normalizeAllowedChatIds(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const out = [];
  values.forEach((raw) => {
    const s = String(raw ?? '').trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

export function validateAllowedChatIds(values) {
  const ids = normalizeAllowedChatIds(values);
  const invalid = ids.filter((id) => !isValidTelegramChatId(id));
  if (invalid.length) {
    return `Invalid chat ID${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`;
  }
  return null;
}

export { TRIGGER_LABELS };
